import type { JSX } from 'preact';
import { FavoriteStar } from './FavoriteStar';
import { useMemo, useState } from 'preact/hooks';
import { EmptyState, FaultState } from './FaultState';
import { useWrite } from './MoneyForms';
import { useResource } from '../lib/store';
import { authedGet } from '../lib/apiClient';
import { LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { clinicToday } from '../lib/clinicDay';
import { compareByLastName } from '../lib/personName';
import {
  SERVICE_COMPLIANCE_KEY, fetchServiceCompliance, addServiceCompliance,
  updateServiceCompliance, deleteServiceCompliance, reloadServiceCompliance,
  alertDate, daysUntilAlert, isDueSoon, isoToMd,
  type ServiceComplianceRow,
} from '../lib/serviceCompliance';

// Tomer 2026-09-08: pre-employment, not a compliance line — Employment
// Verification is dropped from SPC's item types (N-081).
const ITEM_TYPES = [
  'Background Check',
  'License/Education Verification',
  'ME Central Verification',
];

const STAFF_ROLES = new Set(['LMT', 'EST', 'STRETCH']);

interface RosterPerson {
  id: number;
  full_name: string;
  file_no?: string | null;
  role: string | null;
  home_location_code: string;
  status: string | null;
}

function blank(clinic: string) {
  return {
    id: 0,
    fullName: '',
    employeeId: '' as string,
    position: '',
    hireDate: '',
    verificationType: 'License/Education Verification',
    verificationDetail: '',
    dateOrdered: '',
    dateConfirmed: '',
    renewalDate: '',
    expirationDate: '',
    dueDate: '',
    locationCode: clinic,
  };
}

function timing(days: number | null): string {
  if (days == null) return '';
  if (days < 0) return `overdue ${-days} day${-days === 1 ? '' : 's'}`;
  if (days === 0) return 'due today';
  return `in ${days} days`;
}

export function ServiceCompliance({ clinic, pinKey }: { clinic: string; pinKey?: string }): JSX.Element {
  const data = useResource(SERVICE_COMPLIANCE_KEY, fetchServiceCompliance);
  const roster = useResource<{ people: RosterPerson[] }>(
    'people-roster',
    () => authedGet('/api/people'),
  );
  const save = useWrite();
  const [form, setForm] = useState(() => blank(clinic));
  const today = clinicToday();

  const staff = useMemo(() => (
    (roster.status === 'ready' ? roster.data.people ?? [] : [])
      .filter((p) =>
        p.home_location_code === clinic
        && p.status === 'ACTIVE'
        && STAFF_ROLES.has((p.role || '').toUpperCase()))
      .sort((a, b) => compareByLastName(a.full_name, b.full_name))
  ), [roster, clinic]);

  if (data.status === 'loading') return <EmptyState label="Loading SPC…" />;
  if (data.status === 'fault') return <FaultState name="could not load service compliance" detail={data.error} />;

  const rows = (data.data.rows ?? [])
    .filter((r) => r.locationCode === clinic)
    .slice()
    .sort((a, b) => {
      const da = daysUntilAlert(a, today);
      const db = daysUntilAlert(b, today);
      const aDue = da != null && da <= 45;
      const bDue = db != null && db <= 45;
      if (aDue !== bDue) return aDue ? -1 : 1;
      if (aDue && bDue && da != null && db != null && da !== db) return da - db;
      const name = compareByLastName(a.fullName, b.fullName);
      if (name) return name;
      return a.verificationType.localeCompare(b.verificationType);
    });

  function patch(next: Partial<typeof form>): void {
    setForm((prev) => ({ ...prev, ...next }));
  }

  function pickStaff(value: string): void {
    if (!value) {
      patch({ fullName: '', employeeId: '', position: '' });
      return;
    }
    const person = staff.find((p) => String(p.id) === value);
    if (!person) {
      patch({ fullName: value, employeeId: '' });
      return;
    }
    const role = (person.role || '').toUpperCase();
    const position = role === 'EST' ? 'Esthetician'
      : role === 'STRETCH' ? 'Stretch Service Provider'
        : role === 'LMT' ? 'Therapist'
          : person.role || '';
    patch({ fullName: person.full_name, employeeId: String(person.id), position });
  }

  function startEdit(row: ServiceComplianceRow): void {
    patch({
      id: row.id,
      fullName: row.fullName,
      employeeId: row.employeeId != null ? String(row.employeeId) : '',
      position: row.position,
      hireDate: row.hireDate || '',
      verificationType: row.verificationType,
      verificationDetail: row.verificationDetail,
      dateOrdered: row.dateOrdered || '',
      dateConfirmed: row.dateConfirmed || '',
      renewalDate: row.renewalDate || '',
      expirationDate: row.expirationDate || '',
      dueDate: row.dueDate || '',
    });
  }

  async function submit(): Promise<void> {
    const body = {
      locationCode: clinic,
      employeeId: form.employeeId ? Number(form.employeeId) : null,
      fullName: form.fullName.trim(),
      position: form.position.trim(),
      hireDate: form.hireDate,
      verificationType: form.verificationType,
      verificationDetail: form.verificationDetail.trim(),
      dateOrdered: form.dateOrdered,
      dateConfirmed: form.dateConfirmed,
      renewalDate: form.renewalDate,
      expirationDate: form.expirationDate,
      // Blank stays blank; the alert falls to expiration, then renewal (#24).
      dueDate: form.dueDate,
    };
    await save.run(async () => {
      if (form.id) await updateServiceCompliance(form.id, body);
      else await addServiceCompliance(body);
      setForm(blank(clinic));
      await reloadServiceCompliance();
    });
  }

  async function remove(row: ServiceComplianceRow): Promise<void> {
    await save.run(async () => {
      await deleteServiceCompliance(row.id);
      if (form.id === row.id) setForm(blank(clinic));
      await reloadServiceCompliance();
    });
  }

  return (
    <div class="card" data-testid="service-compliance">
      <div class="section-head">
        <div class="section-head-row">
          <h2>SPC — {LOCATION_NAMES[clinic as LocationCode] ?? clinic}</h2>
          <div class="section-head-actions">{pinKey ? <FavoriteStar itemKey={pinKey} /> : null}</div>
        </div>
        <p>License, background, and verification items. Rows due in 45 days are highlighted.</p>
      </div>

      <form
        class="service-point-form"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}
      >
        <label>
          LMT/EST
          <select
            aria-label="LMT/EST"
            value={form.employeeId}
            onChange={(e) => pickStaff((e.target as HTMLSelectElement).value)}
          >
            <option value="">{form.fullName && !form.employeeId ? form.fullName : 'Select…'}</option>
            {staff.map((p) => (
              <option key={p.id} value={String(p.id)}>
                {p.full_name} ({p.role})
              </option>
            ))}
          </select>
        </label>
        <label>
          Name
          <input
            required
            value={form.fullName}
            onInput={(e) => patch({ fullName: (e.target as HTMLInputElement).value, employeeId: '' })}
          />
        </label>
        <label>
          Position
          <input value={form.position} onInput={(e) => patch({ position: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Item
          <select
            required
            value={form.verificationType}
            onChange={(e) => patch({ verificationType: (e.target as HTMLSelectElement).value })}
          >
            {ITEM_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>
          Detail
          <input value={form.verificationDetail} onInput={(e) => patch({ verificationDetail: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Hire Date
          <input type="date" value={form.hireDate} onInput={(e) => patch({ hireDate: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Date Ordered
          <input type="date" value={form.dateOrdered} onInput={(e) => patch({ dateOrdered: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Date Confirmed
          <input type="date" value={form.dateConfirmed} onInput={(e) => patch({ dateConfirmed: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Renewal Date
          <input type="date" value={form.renewalDate} onInput={(e) => patch({ renewalDate: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Expiration Date
          <input type="date" value={form.expirationDate} onInput={(e) => patch({ expirationDate: (e.target as HTMLInputElement).value })} />
        </label>
        <label>
          Due Date
          <input type="date" value={form.dueDate} onInput={(e) => patch({ dueDate: (e.target as HTMLInputElement).value })} />
        </label>
        <div class="service-point-actions">
          <button type="submit" class="btn primary" disabled={save.busy}>
            {form.id ? 'Save' : 'Add'}
          </button>
          {form.id ? (
            <button type="button" class="btn" disabled={save.busy} onClick={() => setForm(blank(clinic))}>
              Cancel
            </button>
          ) : null}
        </div>
        {save.error ? <p class="bad" role="alert">{save.error}</p> : null}
      </form>

      {rows.length === 0 ? (
        <p class="empty">No service compliance items for this clinic yet.</p>
      ) : (
        <table class="data" data-testid="service-compliance-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Item</th>
              <th>Renewal</th>
              <th>Expiration</th>
              <th>Due</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const days = daysUntilAlert(r, today);
              const soon = isDueSoon(r, today);
              const overdue = days != null && days < 0;
              return (
                <tr
                  key={r.id}
                  class={overdue ? 'service-compliance-overdue' : soon ? 'service-compliance-due' : undefined}
                  data-due={soon ? 'true' : 'false'}
                >
                  <td>
                    <b>{r.fullName}</b>
                    <div>{r.position}</div>
                  </td>
                  <td>
                    {r.verificationType}
                    {r.verificationDetail ? <div>{r.verificationDetail}</div> : null}
                  </td>
                  <td>{isoToMd(r.renewalDate)}</td>
                  <td>{isoToMd(r.expirationDate)}</td>
                  <td>
                    {isoToMd(alertDate(r))}
                    {soon ? <div>{timing(days)}</div> : null}
                  </td>
                  <td>
                    <button type="button" class="btn" disabled={save.busy} onClick={() => startEdit(r)}>Edit</button>
                    <button type="button" class="btn" disabled={save.busy} onClick={() => { void remove(r); }}>Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
